import React, { useState, useMemo } from "react";
import {
  Box,
  VStack,
  Select,
  Button,
  Input,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Checkbox,
  Center,
  Spinner,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Divider,
} from "@chakra-ui/react";
import { AddIcon, ChevronDownIcon } from "@chakra-ui/icons";
import {
  ColumnType,
  AggregationType,
  FilterOperator,
  aggregationTypeToJSON,
  filterOperatorToJSON,
} from "build-ts/enums";
import { ColumnInfo, FilterProto, Header, ObservationColumnProto } from "build-ts/metadata";
import GlobalEntity from "containers/batch/GlobalEntity";
import { DateFilter } from "build-ts/relative_date";
import { DateFilterCategory } from "utils/enum";
import { getDateFilterName } from "utils/nameUtils";
import { getOperatorSymbol } from "utils/analysisUtils";

interface ObservationProps {
  type: "observation" | "filter";
  modelName?: string | undefined;
  columns: ColumnInfo[];
  columnNames?: string[];
  selectedObservationColumn: Header | null;
  selectedFilterColumn: ColumnInfo | null;
  selectedAggregation?: AggregationType;
  selectedOperator?: FilterOperator;
  inputValue: string;
  selectedColumnValues: string[];
  selectedFilterColumns: FilterProto[];
  selectedObservationColumns: ObservationColumnProto[];
  dateFilter: DateFilter | undefined;
  dateFilterCategory: DateFilterCategory | undefined;
  setDateFilter: React.Dispatch<React.SetStateAction<DateFilter | undefined>>;
  setDateFilterCategory: React.Dispatch<React.SetStateAction<DateFilterCategory>>;
  handleObservationColumnChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  handleFilterColumnChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  handleAggregationChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  handleOperatorChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  handleValueChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleAddObservationColumn: () => void;
  handleAddFilterColumn: () => void;
  handleToggle: (value: string) => void;
  handleDeleteObservationColumn: (index: number) => void;
  handleDeleteFilterColumn: (index: number) => void;
  isLoading: boolean;
  compact?: boolean;
}

const Observation: React.FC<ObservationProps> = ({
  type,
  modelName,
  columns,
  columnNames,
  selectedObservationColumn,
  selectedFilterColumn,
  selectedAggregation,
  selectedOperator,
  inputValue,
  selectedColumnValues,
  selectedFilterColumns,
  selectedObservationColumns,
  handleObservationColumnChange,
  handleFilterColumnChange,
  handleAggregationChange,
  handleOperatorChange,
  handleValueChange,
  handleAddObservationColumn,
  handleAddFilterColumn,
  handleToggle,
  handleDeleteObservationColumn,
  handleDeleteFilterColumn,
  dateFilter,
  setDateFilter,
  dateFilterCategory,
  setDateFilterCategory,
  isLoading,
  compact,
}) => {
  const shellCompact = compact ?? false;
  const fs = shellCompact ? "xs" : "sm";
  const vs = shellCompact ? 3 : 4;
  const controlSize = shellCompact ? "sm" : "md";

  const [columnSearchTerm, setColumnSearchTerm] = useState("");
  const [valueSearchTerm, setValueSearchTerm] = useState("");

  const measureColumns = useMemo(
    () => columns?.filter((c) => c.column_type === ColumnType.MEASURE) ?? [],
    [columns]
  );

  const filteredMeasureColumns = useMemo(() => {
    if (!columnSearchTerm.trim()) return measureColumns;
    return measureColumns.filter((c) =>
      c.header?.name?.toLowerCase().includes(columnSearchTerm.toLowerCase())
    );
  }, [measureColumns, columnSearchTerm]);

  const filteredAllColumns = useMemo(() => {
    if (!columnSearchTerm.trim()) return columns ?? [];
    return (columns ?? []).filter((c) =>
      c.header?.name?.toLowerCase().includes(columnSearchTerm.toLowerCase())
    );
  }, [columns, columnSearchTerm]);

  const filteredColumnNames = useMemo(() => {
    if (!columnNames) return [];
    if (!valueSearchTerm.trim()) return columnNames;
    return columnNames.filter((option) => {
      const displayValue = option === null ? "{Null}" : option.toString();
      return displayValue.toLowerCase().includes(valueSearchTerm.toLowerCase());
    });
  }, [columnNames, valueSearchTerm]);

  const makeSelectEvent = (value: string): React.ChangeEvent<HTMLSelectElement> =>
    ({ target: { value } } as React.ChangeEvent<HTMLSelectElement>);

  const renderAggregation = () => (
    <VStack spacing={vs} align="stretch" width="100%">
      <Text fontSize={fs} fontWeight="700" color="gray.700" mb={0}>
        Aggregation
      </Text>

      {/* Searchable column dropdown - MEASURE only */}
      <Menu>
        <MenuButton
          as={Button}
          width="100%"
          size={controlSize}
          rightIcon={<ChevronDownIcon />}
          fontSize={fs}
          bg="white"
          border="1px"
          borderColor="gray.300"
          borderRadius="md"
          fontWeight="normal"
          _hover={{ bg: "purple.50" }}
          textAlign="left"
          color={selectedObservationColumn ? "gray.800" : "gray.400"}
        >
          {selectedObservationColumn?.name ?? "Select Column"}
        </MenuButton>
        <MenuList maxHeight="260px" overflowY="auto" zIndex={10}>
          <Box p={2} pb={1}>
            <Input
              placeholder="Search columns..."
              value={columnSearchTerm}
              onChange={(e) => setColumnSearchTerm(e.target.value)}
              size="sm"
              borderColor="gray.300"
              _focus={{ borderColor: "purple.400" }}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>
          {filteredMeasureColumns.map((col) => (
            <MenuItem
              key={col.header?.id}
              onClick={() => {
                handleObservationColumnChange(makeSelectEvent(col.header?.id ?? ""));
                setColumnSearchTerm("");
              }}
              fontSize={fs}
              bg={selectedObservationColumn?.id === col.header?.id ? "purple.50" : undefined}
            >
              {col.header?.name}
            </MenuItem>
          ))}
          {filteredMeasureColumns.length === 0 && (
            <Box p={2} color="gray.500" fontSize={fs}>
              No columns found
            </Box>
          )}
        </MenuList>
      </Menu>

      {/* Aggregation function select */}
      <Select
        placeholder="Aggregation Function"
        onChange={handleAggregationChange}
        value={selectedAggregation !== undefined ? aggregationTypeToJSON(selectedAggregation) : ""}
        fontSize={fs}
        size={controlSize}
        bg="white"
        borderColor="gray.300"
      >
        <option value="SUM">SUM</option>
        <option value="AVERAGE">AVERAGE</option>
        <option value="COUNT">COUNT</option>
        <option value="UNIQUE_COUNT">UNIQUE_COUNT</option>
        <option value="GROWTH">GROWTH</option>
        <option value="MIN">MIN</option>
        <option value="MAX">MAX</option>
        <option value="NO_AGGREGATION">NO_AGGREGATION</option>
      </Select>

      <Button
        colorScheme="purple"
        onClick={handleAddObservationColumn}
        w="100%"
        size={shellCompact ? "sm" : "md"}
        leftIcon={<AddIcon boxSize="0.9rem" />}
        fontWeight="bold"
        isDisabled={selectedObservationColumn === null || selectedAggregation === undefined}
      >
        Include
      </Button>

      {/* Added observations as purple tags */}
      {selectedObservationColumns.length > 0 && (
        <>
          <Divider />
          <Wrap spacing={2}>
            {selectedObservationColumns.map((obs, index) => (
              <WrapItem key={index}>
                <Tag
                  size="sm"
                  fontSize="xs"
                  bg="purple.50"
                  border="1px"
                  borderColor="purple.300"
                  color="gray.800"
                  py={1}
                >
                  <TagLabel>
                    {aggregationTypeToJSON(obs.aggregation!).toUpperCase()}(
                    {obs.logical_column_header?.name})
                  </TagLabel>
                  <TagCloseButton onClick={() => handleDeleteObservationColumn(index)} />
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        </>
      )}

      <VStack align="flex-start" spacing={1} w="100%">
        <Text fontSize={fs} fontWeight="700" color="gray.700" mb={0}>From data model</Text>
        <Tag
          size={compact ? "sm" : "md"}
          colorScheme="purple"
          variant="outline"
          maxW="100%"
          borderRadius="md"
        >
          <TagLabel whiteSpace="normal" wordBreak="break-word">
            {modelName}
          </TagLabel>
        </Tag>
      </VStack>
    </VStack>
  );

  const renderFilter = () => {
    const isAttribute = selectedFilterColumn?.column_type === ColumnType.ATTRIBUTE;
    const isTemporal = selectedFilterColumn?.column_type === ColumnType.TEMPORAL;
    const isMeasure = selectedFilterColumn?.column_type === ColumnType.MEASURE;

    const isAddDisabled =
      selectedFilterColumn === null ||
      (isAttribute
        ? selectedOperator === undefined || selectedColumnValues.length === 0
        : isTemporal
          ? !dateFilter
          : selectedOperator === undefined || !inputValue.trim());

    return (
      <VStack spacing={vs} align="stretch" width="100%">
        <Text fontSize={fs} fontWeight="700" color="gray.700" mb={0}>
          Filters
        </Text>

        {/* Searchable column dropdown - all columns */}
        <Menu>
          <MenuButton
            as={Button}
            width="100%"
            size={controlSize}
            rightIcon={<ChevronDownIcon />}
            fontSize={fs}
            bg="white"
            border="1px"
            borderColor="gray.300"
            borderRadius="md"
            fontWeight="normal"
            _hover={{ bg: "purple.50" }}
            textAlign="left"
            color={selectedFilterColumn ? "gray.800" : "gray.400"}
          >
            {selectedFilterColumn?.header?.name ?? "Select Column"}
          </MenuButton>
          <MenuList maxHeight="260px" overflowY="auto" zIndex={10}>
            <Box p={2} pb={1}>
              <Input
                placeholder="Search columns..."
                value={columnSearchTerm}
                onChange={(e) => setColumnSearchTerm(e.target.value)}
                size="sm"
                borderColor="gray.300"
                _focus={{ borderColor: "purple.400" }}
                onClick={(e) => e.stopPropagation()}
              />
            </Box>
            {filteredAllColumns.map((col) => (
              <MenuItem
                key={col.header?.id}
                onClick={() => {
                  handleFilterColumnChange(makeSelectEvent(col.header?.id ?? ""));
                  setColumnSearchTerm("");
                }}
                fontSize={fs}
                bg={
                  selectedFilterColumn?.header?.id === col.header?.id
                    ? "purple.50"
                    : undefined
                }
              >
                {col.header?.name}
              </MenuItem>
            ))}
            {filteredAllColumns.length === 0 && (
              <Box p={2} color="gray.500" fontSize={fs}>
                No columns found
              </Box>
            )}
          </MenuList>
        </Menu>

        {/* Value input area - depends on column type */}
        {selectedFilterColumn && (
          isLoading ? (
            <Center py={2}>
              <Spinner size="sm" color="purple.500" thickness="2px" />
            </Center>
          ) : isAttribute ? (
            /* Operator (= / !=) + multiselect for ATTRIBUTE */
            <>
              <Select
                placeholder="Operator"
                onChange={handleOperatorChange}
                value={
                  selectedOperator !== undefined ? filterOperatorToJSON(selectedOperator) : ""
                }
                fontSize={fs}
                size={controlSize}
                bg="white"
                borderColor="gray.300"
              >
                <option value="EQ">=</option>
                <option value="NE">!=</option>
              </Select>
              <Menu closeOnSelect={false}>
                <MenuButton
                  as={Button}
                  width="100%"
                  size={controlSize}
                  rightIcon={<ChevronDownIcon />}
                  fontSize={fs}
                  bg="white"
                  border="1px"
                  borderColor="gray.300"
                  borderRadius="md"
                  fontWeight="normal"
                  _hover={{ bg: "purple.50" }}
                  textAlign="left"
                  color={selectedColumnValues.length > 0 ? "gray.800" : "gray.400"}
                >
                  {selectedColumnValues.length > 0
                    ? `Selected (${selectedColumnValues.length})`
                    : "Select Values"}
                </MenuButton>
                <MenuList maxHeight="260px" overflowY="auto" zIndex={10}>
                  <Box p={2} pb={1}>
                    <Input
                      placeholder="Search values..."
                      value={valueSearchTerm}
                      onChange={(e) => setValueSearchTerm(e.target.value)}
                      size="sm"
                      borderColor="gray.300"
                      _focus={{ borderColor: "purple.400" }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Box>
                  {filteredColumnNames.map((option, index) => (
                    <MenuItem key={index} minH="40px">
                      <Checkbox
                        isChecked={selectedColumnValues.includes(option)}
                        onChange={() => handleToggle(option)}
                        colorScheme="purple"
                      >
                        {option === null ? "{Null}" : option.toString()}
                      </Checkbox>
                    </MenuItem>
                  ))}
                  {filteredColumnNames.length === 0 && valueSearchTerm.trim() && (
                    <Box p={2} color="gray.500" fontSize={fs}>
                      No options found
                    </Box>
                  )}
                </MenuList>
              </Menu>
            </>
          ) : isTemporal ? (
            /* GlobalEntity inline for TEMPORAL - no operator */
            <GlobalEntity
              setDateFilter={setDateFilter}
              dateFilter={dateFilter}
              setDateFilterCategory={setDateFilterCategory}
              dateFilterCategory={dateFilterCategory}
              id="observation"
            />
          ) : isMeasure ? (
            /* Operator + input for MEASURE */
            <>
              <Select
                placeholder="Operator"
                onChange={handleOperatorChange}
                value={
                  selectedOperator !== undefined ? filterOperatorToJSON(selectedOperator) : ""
                }
                fontSize={fs}
                size={controlSize}
                bg="white"
                borderColor="gray.300"
              >
                <option value="EQ">=</option>
                <option value="NE">!=</option>
                <option value="GT">&gt;</option>
                <option value="GTE">&gt;=</option>
                <option value="LT">&lt;</option>
                <option value="LTE">&lt;=</option>
              </Select>
              <Input
                type="text"
                placeholder="Value"
                value={inputValue}
                onChange={handleValueChange}
                fontSize={fs}
                size={controlSize}
                bg="white"
                borderColor="gray.300"
              />
            </>
          ) : null
        )}

        <Button
          colorScheme="purple"
          onClick={handleAddFilterColumn}
          w="100%"
          size={shellCompact ? "sm" : "md"}
          leftIcon={<AddIcon boxSize="0.9rem" />}
          fontWeight="bold"
          isDisabled={isAddDisabled}
        >
          Include
        </Button>

        {/* Added filters as purple tags */}
        {selectedFilterColumns.length > 0 && (
          <>
            <Divider />
            <Wrap spacing={2}>
              {selectedFilterColumns.map((filter, index) => {
                let displayText = "";
                if (filter.date_filter) {
                  displayText = `${filter.logical_column_header?.name}: ${getDateFilterName(filter.date_filter)}`;
                } else if (
                  filter.logical_column_header &&
                  filter.values &&
                  filter.values.length > 0
                ) {
                  const symbol = getOperatorSymbol(filter.operator!);
                  displayText = `${filter.logical_column_header.name} ${symbol} ${filter.values.join(", ")}`;
                }
                return displayText ? (
                  <WrapItem key={index}>
                    <Tag
                      size="sm"
                      fontSize="xs"
                      bg="purple.50"
                      border="1px"
                      borderColor="purple.300"
                      color="gray.800"
                      py={1}
                    >
                      <TagLabel>{displayText}</TagLabel>
                      <TagCloseButton onClick={() => handleDeleteFilterColumn(index)} />
                    </Tag>
                  </WrapItem>
                ) : null;
              })}
            </Wrap>
          </>
        )}
      </VStack>
    );
  };

  return type === "observation" ? renderAggregation() : renderFilter();
};

export default Observation;
